import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { debounce, toggleAreaExpanded } from '../utils';
import { initializeSelect } from '../manuals/form';

export function initializeTable({
    tableElement = '',
    filtersElement = '',
    tabsElement = '',
    ajaxURL = '',
    columns = [],
    rowContextMenu = null,
    height = 'calc(100vh - 25rem)',
    maxHeight = '66rem',
    rowFormatter = null,
    rowClickPopup = undefined,
    initialFilter = [],
    forcedFilter = [],
    initialSort = [],
    layout = 'fitColumns',
    handleFiltering = true,
    pillsConfig = null,
    dataMode = 'remote',
    filterType = '=',
    ...rest
} = {}) {
    if (rowContextMenu && Array.isArray(rowContextMenu)) {
        rowContextMenu = rowContextMenu.filter((item) => item);
    }

    if (forcedFilter) {
        initialFilter = [...forcedFilter, ...initialFilter];
    }

    if (tabsElement) {
        const activeTab = tabsElement.querySelector('.active');
        if (activeTab) {
            initialFilter.push({
                field: activeTab.dataset.filter,
                type: '=',
                value: activeTab.dataset.value,
            });
        }
    }

    const table = new Tabulator(tableElement, {
        ajaxURL: ajaxURL,
        ajaxURLGenerator:
            dataMode === 'remote'
                ? function (url, config, params) {
                      params['limit'] = params.size;
                      params['offset'] = (params.page - 1) * params.size;
                      const urlParams = {
                          limit: params.size,
                          offset: (params.page - 1) * params.size,
                      };
                      if (params.sort.length > 0) {
                          const order =
                              params.sort[0].dir === 'asc'
                                  ? params.sort[0].field
                                  : `-${params.sort[0].field}`;
                          urlParams['order'] = order.replaceAll('.', '__');
                      }
                      if (params.filter.length > 0) {
                          params.filter.forEach((f) => {
                              if (f.value.start || f.value.end) {
                                  if (f.value.start) {
                                      urlParams[
                                          `${f.field.replaceAll('.', '__')}__gte`
                                      ] = f.value.start;
                                  }
                                  if (f.value.end) {
                                      urlParams[
                                          `${f.field.replaceAll('.', '__')}__lte`
                                      ] = f.value.end;
                                  }
                              } else {
                                  urlParams[f.field.replaceAll('.', '__')] =
                                      f.value;
                              }
                          });
                      }
                      const urlParamsString = new URLSearchParams(
                          urlParams,
                      ).toString();
                      this.externalEvents.dispatch(
                          'ajaxURLGenerated',
                          url,
                          urlParamsString,
                      );
                      return url + '?' + urlParamsString;
                  }
                : undefined,
        ajaxResponse:
            dataMode === 'remote'
                ? function (url, params, response) {
                      const accessorColumns = columns.filter(
                          (col) => typeof col.accessor === 'function',
                      );
                      this.externalEvents.dispatch(
                          'ajaxResponseLoaded',
                          response,
                      );
                      if (accessorColumns.length === 0) {
                          if (response.items) {
                              return {
                                  last_page: Math.ceil(
                                      response.count / params.size,
                                  ),
                                  last_row: response.count,
                                  data: response.items,
                              };
                          }
                          return response;
                      }
                      const transformedItems = response.items.map((item) => {
                          const newItem = { ...item };
                          accessorColumns.forEach((col) => {
                              newItem[col.field] = col.accessor(
                                  null,
                                  item,
                                  'data',
                              );
                          });
                          return newItem;
                      });
                      return {
                          last_page: Math.ceil(response.count / params.size),
                          last_row: response.count,
                          data: transformedItems,
                      };
                  }
                : function (url, params, response) {
                      return response.items;
                  },
        height: height,
        maxHeight: maxHeight,
        layout: layout,
        progressiveLoad: dataMode === 'remote' ? 'scroll' : undefined,
        paginationSize: 50,
        filterMode: dataMode,
        initialFilter: initialFilter,
        initialSort: initialSort,
        sortMode: dataMode,
        selectableRows: dataMode === 'remote' ? false : undefined,
        columns: columns,
        rowContextMenu: rowContextMenu,
        rowFormatter: rowFormatter,
        rowClickPopup: rowClickPopup,
        placeholder: function () {
            const activeFiltersCount = this.getFilters().filter(
                (f) =>
                    !initialFilter.some(
                        (ff) =>
                            ff.field === f.field &&
                            ff.type === f.type &&
                            ff.value === f.value,
                    ),
            ).length;
            if (activeFiltersCount === 0) {
                return gettext('Brak danych');
            }
            const container = document.createElement('div');
            container.className = 'tabulator-placeholder-contents';
            container.textContent = gettext('Brak pasujących danych');
            const clearButton = document.createElement('button');
            clearButton.className = 'clear-btn';
            clearButton.textContent = gettext('Wyczyść filtry');
            const that = this;
            clearButton.addEventListener('click', function () {
                if (filtersElement) {
                    filtersElement
                        .querySelectorAll(
                            'input[type="text"], input[type="search"], input[type="number"], input[type="date"], textarea, select',
                        )
                        .forEach((field) => {
                            let eventNameToDispatch =
                                field.tagName.toLowerCase() === 'select' ||
                                field.type === 'date'
                                    ? 'change'
                                    : 'input';

                            if (field._choices) {
                                initializeSelect(field, {}, true);
                            } else if (
                                field.multiple &&
                                field.tagName === 'SELECT'
                            ) {
                                Array.from(field.options).forEach(
                                    (option) => (option.selected = false),
                                );
                            } else {
                                field.value = '';
                            }

                            field.dispatchEvent(
                                new Event(eventNameToDispatch, {
                                    bubbles: true,
                                }),
                            );
                        });
                }
                that.clearFilter();
                filtersElement.querySelectorAll('input').forEach((input) => {
                    input.value = '';
                });
            });
            container.appendChild(clearButton);
            return container;
        },
        ...rest,
    });

    if (pillsConfig && pillsConfig.containerSelector) {
        const pillsContainer = document.querySelector(
            pillsConfig.containerSelector,
        );

        if (pillsContainer) {
            function getFriendlyFilterLabel(fieldName) {
                const fieldElement = document.getElementById(`id_${fieldName}`);
                if (fieldElement) {
                    const label = document.querySelector(
                        `label[for="id_${fieldName}"]`,
                    );
                    return label
                        ? label.textContent
                              .trim()
                              .split(' ')[0]
                              .replace(/:+/g, '')
                        : fieldName;
                }
                return fieldName;
            }

            const renderFilterPills = (currentFilters) => {
                pillsContainer.innerHTML = '';

                currentFilters.forEach((filter) => {
                    const field = filter.field;
                    const value = filter.value;
                    let labelText = '';
                    let pillText = '';
                    let isSpecialPill = false;
                    let specialConfig = null;

                    if (
                        (pillsConfig.ignoreFields &&
                            pillsConfig.ignoreFields.includes(field)) ||
                        field.endsWith('_alt')
                    ) {
                        return;
                    }

                    if (
                        pillsConfig.specialPills &&
                        pillsConfig.specialPills[field]
                    ) {
                        specialConfig = pillsConfig.specialPills[field];
                        if (
                            String(value).toLowerCase() ===
                            String(specialConfig.valueToMatch).toLowerCase()
                        ) {
                            isSpecialPill = true;
                            labelText = specialConfig.labelText;
                            pillText = specialConfig.labelText;
                        } else {
                            isSpecialPill = true;
                        }
                    }

                    if (!isSpecialPill) {
                        if (!value && value !== 0 && value !== false) return;
                        let displayValue = value;
                        const inputEl = document.getElementById(`id_${field}`);

                        if (inputEl && inputEl.tagName === 'SELECT') {
                            if (inputEl && inputEl.tagName === 'SELECT') {
                                const option = Array.from(inputEl.options).find(
                                    (opt) => opt.value == value,
                                );
                                if (option) {
                                    displayValue = option.textContent;
                                }
                            } else {
                                const option = Array.from(inputEl.options).find(
                                    (opt) => opt.value == value,
                                );
                                if (option) displayValue = option.textContent;
                            }
                        }

                        labelText = getFriendlyFilterLabel(field);
                        pillText = `${labelText}: <strong>${displayValue}</strong>`;
                    }

                    if (pillText) {
                        const pill = document.createElement('div');
                        pill.className = 'filter-pill';
                        if (isSpecialPill) {
                            pill.classList.add(
                                `filter-pill-${field.replace(/_/g, '-')}`,
                            );
                        }

                        pill.innerHTML = `
                            <button class="pill-close" data-filter-field="${field}" aria-label="${gettext('Usuń filtr')} ${labelText}">×</button>
                            <span>${pillText}</span>
                        `;
                        pillsContainer.appendChild(pill);
                    }
                });
            };

            pillsContainer.addEventListener('click', (e) => {
                const filters = [...(forcedFilter || [])];
                const closeButton = e.target.closest('.pill-close');
                if (!closeButton) return;

                const fieldToClear = closeButton.dataset.filterField;
                if (!fieldToClear) return;

                let inputId = `id_${fieldToClear}`;
                if (
                    pillsConfig.specialPills &&
                    pillsConfig.specialPills[fieldToClear] &&
                    pillsConfig.specialPills[fieldToClear].filterInputId
                ) {
                    inputId =
                        pillsConfig.specialPills[fieldToClear].filterInputId;
                }

                const fieldElement = document.getElementById(inputId);
                if (fieldElement) {
                    const altFieldElement = document.getElementById(
                        `${inputId}_alt`,
                    );
                    if (altFieldElement) {
                        altFieldElement.value = '';
                        altFieldElement.dispatchEvent(
                            new Event('change', { bubbles: true }),
                        );
                    }

                    if (fieldElement._choices) {
                        fieldElement._choices.removeActiveItems();
                        fieldElement._choices.setChoiceByValue('');
                        window.forms.initializeSelect(fieldElement, null, true);
                    } else if (
                        fieldElement.multiple &&
                        fieldElement.tagName === 'SELECT'
                    ) {
                        Array.from(fieldElement.options).forEach(
                            (option) => (option.selected = false),
                        );
                    } else {
                        fieldElement.value = '';
                    }

                    const eventType =
                        fieldElement.tagName === 'INPUT' &&
                        fieldElement.type !== 'date'
                            ? 'input'
                            : 'change';
                    fieldElement.dispatchEvent(
                        new Event(eventType, { bubbles: true }),
                    );
                }
            });

            table.on('dataFiltered', (filters) => renderFilterPills(filters));

            table.on('tableBuilt', () => {
                setTimeout(() => renderFilterPills(table.getFilters(true)), 0);
            });
        } else {
            console.error(
                'Pill container not found:',
                pillsConfig.containerSelector,
            );
        }
    }
    if (filtersElement) {
        filtersElement.querySelectorAll('select').forEach((select) => {
            initializeSelect(select);
        });
    }
    if (handleFiltering && filtersElement) {
        const setFilters = debounce(() => {
            const filters = [...(forcedFilter || [])];

            if (tabsElement) {
                const activeTab = tabsElement.querySelector('.active');
                if (
                    activeTab &&
                    activeTab.dataset.filter &&
                    activeTab.dataset.value
                ) {
                    filters.push({
                        field: activeTab.dataset.filter,
                        type: '=',
                        value: activeTab.dataset.value,
                    });
                }
            }

            filtersElement
                .querySelectorAll('input, select')
                .forEach((field) => {
                    const fieldId = field.id;
                    if (!fieldId || fieldId.endsWith('_alt')) return;

                    const fieldName = fieldId.startsWith('id_')
                        ? fieldId.substring(3)
                        : field.name;
                    let value;

                    if (field.tagName === 'SELECT' && field._choices) {
                        const choice = field._choices.getValue();
                        if (!choice || choice.value === '') {
                            return;
                        }
                        if (
                            fieldName === 'is_archived' ||
                            fieldName === 'read'
                        ) {
                            value = choice.value.toLowerCase();
                        } else if (
                            fieldName === 'user_id' ||
                            fieldName === 'target_type' ||
                            fieldName === 'status'
                        ) {
                            value = choice.value;
                        } else {
                            value = choice.label;
                        }
                    } else {
                        value = field.value;
                    }
                    if (value) {
                        const type =
                            field.tagName === 'INPUT' && field.type === 'text'
                                ? 'like'
                                : filterType;
                        filters.push({
                            field: fieldName,
                            type: type,
                            value: value,
                        });
                    }
                });

            table.setFilter(filters);
        }, 300);

        filtersElement.querySelectorAll('input, select').forEach((field) => {
            field.addEventListener('input', setFilters);
            field.addEventListener('change', setFilters);
        });

        if (tabsElement) {
            tabsElement.querySelectorAll('.tab').forEach((tab) => {
                tab.addEventListener('click', function () {
                    const activeTab = tabsElement.querySelector('.active');
                    if (activeTab === tab) {
                        return;
                    }
                    activeTab.classList.remove('active');
                    tab.classList.add('active');
                    setFilters();
                });
            });
        }
    }

    return table;
}

export function initializeTableFilter() {
    const filter_btn = document.querySelector('.filter-btn');
    if (!filter_btn) return;

    filter_btn.addEventListener('click', (e) => {
        toggleAreaExpanded(filter_btn);
        filter_btn.classList.toggle('active');
    });
}
